import { call, put, all, select } from 'redux-saga/effects';
import {
  getKey,
  getKeySha,
  getCollapsedKey,
  types as pullRequestsTypes,
  selectors as pullRequestsSelectors,
} from '../reducers/pullRequests';
import * as Api from '../utils/Api';
import { BatchIssues } from '../constants/Batch';
import { generators } from 'stores';

const getBasePullRequestURL = (
  providerId,
  organizationSlug,
  projectName,
  reviewNumber
) =>
  `repositories/${providerId}/${organizationSlug}/${encodeURIComponent(
    projectName
  )}/pull-requests/${reviewNumber}`;

export function* fetchPullRequest(action) {
  const { providerId, organizationSlug, projectName, reviewNumber } = action;
  if (!providerId || !organizationSlug || !projectName || !reviewNumber) {
    throw new Error('Missing required parameters for fetching a pullrequest');
  }
  const url = getBasePullRequestURL(
    providerId,
    organizationSlug,
    projectName,
    reviewNumber
  );
  const response = yield call(Api.get, url);
  if (!response.success) {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_FAILURE,
      error:
        response.data && response.data.error_message
          ? response.data.error_message
          : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_SUCCESS,
      data: response.data,
      key: getKey(providerId, organizationSlug, projectName, reviewNumber),
    });
  }
}

export function* fetchPullRequestCommits(action) {
  const { providerId, organizationSlug, projectName, reviewNumber } = action;
  if (!providerId || !organizationSlug || !projectName || !reviewNumber) {
    throw new Error(
      'Missing required parameters for fetching pullrequest commits'
    );
  }
  const commits = [];
  let url =
    getBasePullRequestURL(
      providerId,
      organizationSlug,
      projectName,
      reviewNumber
    ) + '/commits';
  while (url) {
    const response = yield call(Api.get, url);
    if (!response.success) {
      yield put({
        type: pullRequestsTypes.PULL_REQUEST_COMMITS_FAILURE,
        error:
          response.data && response.data.error_message
            ? response.data.error_message
            : 'Unknown error',
        errorCode: response.status,
      });
      return;
    }
    // add this page of data
    commits.push(...response.data.data);
    url = response.data.next_page_url || null;
  }
  yield put({
    type: pullRequestsTypes.PULL_REQUEST_COMMITS_SUCCESS,
    data: commits,
    key: getKey(providerId, organizationSlug, projectName, reviewNumber),
  });
}

export function* fetchPullRequestComments(action) {
  const { providerId, organizationSlug, projectName, reviewNumber } = action;
  if (!providerId || !organizationSlug || !projectName || !reviewNumber) {
    throw new Error(
      'Missing required parameters for fetching pullrequest comments'
    );
  }
  const comments = [];
  let url =
    getBasePullRequestURL(
      providerId,
      organizationSlug,
      projectName,
      reviewNumber
    ) + '/comments';
  while (url) {
    const response = yield call(Api.get, url);
    if (!response.success) {
      yield put({
        type: pullRequestsTypes.PULL_REQUEST_COMMENTS_FAILURE,
        error:
          response.data && response.data.error_message
            ? response.data.error_message
            : 'Unknown error',
        errorCode: response.status,
      });

      return;
    }
    // add this page of data
    comments.push(...response.data.data);
    url = response.data.next_page_url || null;
  }
  yield put({
    type: pullRequestsTypes.PULL_REQUEST_COMMENTS_SUCCESS,
    data: comments,
    key: getKey(providerId, organizationSlug, projectName, reviewNumber),
  });
}

export function* fetchPullRequestCommentSnippets(action) {
  const { providerId, organizationSlug, projectName, reviewNumber } = action;
  if (!providerId || !organizationSlug || !projectName || !reviewNumber) {
    throw new Error(
      'Missing required parameters for fetching pullrequest comment snippets'
    );
  }
  const snippets = [];
  let url =
    getBasePullRequestURL(
      providerId,
      organizationSlug,
      projectName,
      reviewNumber
    ) + '/comment-snippets';
  while (url) {
    const response = yield call(Api.get, url);
    if (!response.success) {
      yield put({
        type: pullRequestsTypes.PULL_REQUEST_COMMENT_SNIPPETS_FAILURE,
        error:
          response.data && response.data.error_message
            ? response.data.error_message
            : 'Unknown error',
        errorCode: response.status,
      });
      return;
    }
    // add this page of data
    snippets.push(...response.data.data);
    url = response.data.next_page_url || null;
  }
  yield put({
    type: pullRequestsTypes.PULL_REQUEST_COMMENT_SNIPPETS_SUCCESS,
    data: snippets,
    key: getKey(providerId, organizationSlug, projectName, reviewNumber),
  });
}

export function* fetchPullRequestReviews(action) {
  const { providerId, organizationSlug, projectName, reviewNumber } = action;
  if (!providerId || !organizationSlug || !projectName || !reviewNumber) {
    throw new Error(
      'Missing required parameters for fetching pullrequest reviews'
    );
  }
  const reviews = [];
  let url =
    getBasePullRequestURL(
      providerId,
      organizationSlug,
      projectName,
      reviewNumber
    ) + '/reviews';
  while (url) {
    const response = yield call(Api.get, url);
    if (!response.success) {
      yield put({
        type: pullRequestsTypes.PULL_REQUEST_REVIEWS_FAILURE,
        error:
          response.data && response.data.error_message
            ? response.data.error_message
            : 'Unknown error',
        errorCode: response.status,
      });
      return;
    }
    // add this page of data
    reviews.push(...response.data.data);
    url = response.data.next_page_url || null;
  }
  yield put({
    type: pullRequestsTypes.PULL_REQUEST_REVIEWS_SUCCESS,
    data: reviews,
    key: getKey(providerId, organizationSlug, projectName, reviewNumber),
  });
}

export function* fetchPullRequestStaticResults(action) {
  const { providerId, organizationSlug, projectName, reviewNumber, sha } =
    action;
  if (!providerId || !organizationSlug || !projectName || !reviewNumber) {
    throw new Error('Missing required parameters for fetching static job');
  }
  const shaParam = sha ? `/${sha}` : '';
  const url =
    getBasePullRequestURL(
      providerId,
      organizationSlug,
      projectName,
      reviewNumber
    ) + `/latest-static-job${shaParam}`;
  const response = yield call(Api.get, url);
  if (!response.success) {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_STATIC_JOB_FAILURE,
      error:
        response.data && response.data.error_message
          ? response.data.error_message
          : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    const prevJob = yield select(
      pullRequestsSelectors.pullRequestStaticJobForOrgAndRepo,
      providerId,
      organizationSlug,
      projectName,
      reviewNumber,
      sha
    );

    const job = response.data;

    yield put({
      type: pullRequestsTypes.PULL_REQUEST_STATIC_JOB_SUCCESS,
      job,
      key: getKeySha(
        providerId,
        organizationSlug,
        projectName,
        reviewNumber,
        job.uuid
      ),
    });
    if (!sha) {
      // if there is no sha from the request then also save this as the special 'latest' key
      yield put({
        type: pullRequestsTypes.PULL_REQUEST_STATIC_JOB_SUCCESS,
        job,
        key: getKeySha(
          providerId,
          organizationSlug,
          projectName,
          reviewNumber,
          'latest'
        ),
      });
    }

    if (prevJob && prevJob.completed_at) {
      return;
    }

    // if the job wasn't already complete then fetch results for any tasks that have since completed
    const prevEnginesComplete = prevJob
      ? prevJob.tasks
          .filter((task) => Boolean(task.completed_at))
          .map((task) => task.engine)
      : [];

    yield all(
      job.tasks
        .filter((task) => Boolean(task.completed_at))
        .filter((task) => !task.error)
        .filter((task) => !prevEnginesComplete.includes(task.engine))
        .map((task) =>
          fetchPullRequestStaticTask({
            sha: job.sha,
            jobUUID: job.uuid,
            staticEngine: task.engine,
            providerId,
            organizationSlug,
            projectName,
            reviewNumber,
          })
        )
    );
  }
}

export function* fetchPullRequestStaticTask(action) {
  const {
    sha,
    providerId,
    organizationSlug,
    projectName,
    reviewNumber,
    jobUUID,
    staticEngine,
  } = action;
  if (
    !sha ||
    !providerId ||
    !organizationSlug ||
    !projectName ||
    !reviewNumber ||
    !jobUUID ||
    !staticEngine
  ) {
    throw new Error('Missing required parameters for fetching static task');
  }
  const url = `static-jobs/${jobUUID}/${staticEngine}`;
  const response = yield call(Api.get, url);
  if (!response.success) {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_STATIC_RESULTS_FAILURE,
      error:
        response.data && response.data.error_message
          ? response.data.error_message
          : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    const staticResults = response.data;
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_STATIC_RESULTS_SUCCESS,
      data: staticResults,
      key: getKeySha(
        providerId,
        organizationSlug,
        projectName,
        reviewNumber,
        jobUUID
      ),
      staticEngine,
    });
  }
}

export function* fetchPullRequestStaticResultSnippets(action) {
  const { sha, jobUUID, staticResults, batchSize } = action;
  if (!sha || !jobUUID || !staticResults) {
    throw new Error(
      'Missing required parameters for fetching static results snippets: sha, jobUUID, staticResults'
    );
  }
  const snippets = select(
    pullRequestsSelectors.pullRequestStaticResultsSnippetsForJob,
    jobUUID
  );
  const snippetsToRetrieve = staticResults.reduce((results, staticResult) => {
    const path = staticResult.location.path;
    const line = staticResult.location.line;
    if (!snippets.hasOwnProperty(`${sha}:${path}:${line}`)) {
      results.push({
        path,
        line,
      });
    }
    return results;
  }, []);

  const url = `static-jobs/${jobUUID}/snippets/${sha}`;
  let start = 0;
  let end = batchSize || BatchIssues.BATCH_SIZE;
  let results = snippetsToRetrieve.slice(start, end);
  while (results.length) {
    const body = {
      results,
    };
    const response = yield call(Api.post, url, body);
    if (!response.success) {
      yield put({
        type: pullRequestsTypes.PULL_REQUEST_STATIC_RESULTS_SNIPPETS_FAILURE,
        error:
          response.data && response.data.error_message
            ? response.data.error_message
            : 'Unknown error',
        errorCode: response.status,
      });
      return;
    } else {
      const snippetsWithKeys = response.data.reduce((snippets, result) => {
        snippets[`${sha}:${result.path}:${result.line}`] = result.snippet;
        return snippets;
      }, {});
      yield put({
        type: pullRequestsTypes.PULL_REQUEST_STATIC_RESULTS_SNIPPETS_SUCCESS,
        data: snippetsWithKeys,
        key: jobUUID,
      });
      start = end;
      end += BatchIssues.LARGE_BATCH_SIZE;
      results = snippetsToRetrieve.slice(start, end);
    }
  }
}

export function* fetchPullRequestEvents(action) {
  const { pullRequestUUID } = action;
  if (!pullRequestUUID) {
    throw new Error(
      'Missing required parameters for fetching pullrequest events'
    );
  }
  const events = [];
  let url = `pull-requests/${pullRequestUUID}/events`;
  while (url) {
    const response = yield call(Api.get, url);
    if (!response.success) {
      yield put({
        type: pullRequestsTypes.PULL_REQUEST_EVENTS_FAILURE,
        error:
          response.data && response.data.error_message
            ? response.data.error_message
            : 'Unknown error',
        errorCode: response.status,
      });
      return;
    }
    // add this page of data
    events.push(...response.data.data);
    url = response.data.next_page_url || null;
  }
  yield put({
    type: pullRequestsTypes.PULL_REQUEST_EVENTS_SUCCESS,
    data: events,
    pullRequestUUID,
  });
}

export function* restartStaticJob(action) {
  const { providerId, organizationSlug, projectName, reviewNumber } = action;
  if (!providerId || !organizationSlug || !projectName || !reviewNumber) {
    throw new Error('Missing required parameters for restarting a static job');
  }
  const url =
    getBasePullRequestURL(
      providerId,
      organizationSlug,
      projectName,
      reviewNumber
    ) + `/static-jobs/restart`;
  const response = yield call(Api.post, url);
  if (!response.success) {
    yield put({
      type: pullRequestsTypes.RESTART_STATIC_JOB_FAILURE,
      error:
        response.data && response.data.error_message
          ? response.data.error_message
          : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    // on success, lets trigger a refetch of static results for the latest job to get any updated status
    yield fetchPullRequestStaticResults(action);
    yield put({ type: pullRequestsTypes.RESTART_STATIC_JOB_SUCCESS });
  }
}

export function* rerequestReview(action) {
  const { organization, project, reviewNumber, pullRequestUUID, status } =
    action;
  if (
    !organization ||
    !project ||
    !reviewNumber ||
    !pullRequestUUID ||
    !status
  ) {
    throw new Error('Missing required parameters for re-requesting a review');
  }
  const url =
    getBasePullRequestURL(
      organization.providerId,
      organization.slug,
      project.name,
      reviewNumber
    ) + `/re-request`;
  const response = yield call(Api.post, url);
  if (!response.success) {
    yield put({
      type: pullRequestsTypes.REREQUEST_REVIEW_FAILURE,
      error:
        response.data && response.data.error_message
          ? response.data.error_message
          : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    yield put({
      type: pullRequestsTypes.REREQUEST_REVIEW_SUCCESS,
    });
    yield all([
      put(
        generators.pullRequests.refresh.request({
          uuid: pullRequestUUID,
          projectUUID: project.uuid,
        })
      ),
    ]);
  }
}

export function* fetchStaticTaskErrorLog(action) {
  const { job, task } = action;
  if (!job || !task) {
    throw new Error(
      'Missing required parameters for fetching a pullrequest static task error log'
    );
  }
  const url = `static-jobs/${job.uuid}/${task.engine}/error-log`;
  const response = yield call(Api.get, url, true, false);
  if (!response.success) {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_STATIC_TASK_ERROR_LOG_FAILURE,
      error:
        response.data && response.data.error_message
          ? response.data.error_message
          : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_STATIC_TASK_ERROR_LOG_SUCCESS,
      data: response.data,
      key: `${job.uuid}:${task.engine}`,
    });
  }
}

export function* fetchPullRequestDiff(action) {
  const {
    providerId,
    organizationSlug,
    projectName,
    reviewNumber,
    sha,
    baseSha,
  } = action;
  if (!providerId || !organizationSlug || !projectName || !reviewNumber) {
    throw new Error(
      'Missing required parameters for fetching a pullrequest diff'
    );
  }
  let url =
    getBasePullRequestURL(
      providerId,
      organizationSlug,
      projectName,
      reviewNumber
    ) + '/diff';
  if (baseSha && sha) {
    url += '/' + baseSha + '..' + sha;
  } else if (sha) {
    url += '/' + sha;
  }
  const response = yield call(Api.get, url);
  if (!response.success) {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_DIFF_FAILURE,
      error:
        response.data && response.data.error_message
          ? response.data.error_message
          : 'Unknown error',
      errorCode: response.status,
    });
  } else {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_DIFF_SUCCESS,
      data: response.data,
      key: getKeySha(
        providerId,
        organizationSlug,
        projectName,
        reviewNumber,
        sha
      ),
    });
  }
}

export function* collapseInlineElement(action) {
  const { element, providerId, organizationSlug, projectName, reviewNumber } =
    action;
  if (!element) {
    throw new Error(
      'Missing required parameters for collapsing inline element'
    );
  }
  if (element.type === 'comment') {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_COLLAPSED_COMMENTS_SUCCESS,
      data: element,
      key: getCollapsedKey(element.path, element.uuid),
      reviewKey: getKey(
        providerId,
        organizationSlug,
        projectName,
        reviewNumber
      ),
    });
  }
  if (element.type === 'static') {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_COLLAPSED_STATIC_RESULTS_SUCCESS,
      data: element,
      key: getCollapsedKey(element.location.path, element.fingerprint),
      reviewKey: getKey(
        providerId,
        organizationSlug,
        projectName,
        reviewNumber
      ),
    });
  }
}

export function* expandInlineElement(action) {
  const { element, providerId, organizationSlug, projectName, reviewNumber } =
    action;
  if (!element) {
    throw new Error('Missing required parameters for expanding inline element');
  }
  if (element.type === 'comment') {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_EXPAND_COMMENTS_SUCCESS,
      key: getCollapsedKey(element.path, element.uuid),
      reviewKey: getKey(
        providerId,
        organizationSlug,
        projectName,
        reviewNumber
      ),
    });
  }
  if (element.type === 'static') {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_EXPAND_STATIC_RESULTS_SUCCESS,
      key: getCollapsedKey(element.location.path, element.fingerprint),
      reviewKey: getKey(
        providerId,
        organizationSlug,
        projectName,
        reviewNumber
      ),
    });
  }
}

export function* fetchSnoozedStaticResults(action) {
  const { pullRequestUUID } = action;
  if (!pullRequestUUID) {
    throw new Error(
      'Missing required parameters for fetching snoozed static results.'
    );
  }
  let url = `/pull-requests/${pullRequestUUID}/static-filters`;
  const response = yield call(Api.get, url);
  if (!response.success) {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_SNOOZED_RESULTS_FAILURE,
      error:
        response.data && response.data.error_message
          ? response.data.error_message
          : 'Unknown error',
    });
    return;
  }
  const snoozedResults = response.data.data;
  const snoozedResultsByKey = snoozedResults.reduce((obj, item) => {
    obj[getCollapsedKey(item.path, item.fingerprint)] = item;
    return obj;
  }, {});
  yield put({
    type: pullRequestsTypes.PULL_REQUEST_SNOOZED_RESULTS_SUCCESS,
    data: snoozedResultsByKey,
    key: pullRequestUUID,
  });
}

export function* snoozeStaticResult(action) {
  const {
    pullRequestUUID,
    path,
    fingerprint,
    engine,
    severity,
    issueName,
    displayName,
  } = action;
  if (!pullRequestUUID || !path || !fingerprint || !engine) {
    throw new Error('Missing required parameters for snoozing static result.');
  }
  let url = `/pull-requests/${pullRequestUUID}/static-filters`;
  const body = {
    path,
    fingerprint,
    engine,
    severity,
    issue_name: issueName,
    display_name: displayName,
  };
  const response = yield call(Api.post, url, body);
  if (!response.success) {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_SNOOZE_RESULT_FAILURE,
      error:
        response.data && response.data.error_message
          ? response.data.error_message
          : 'Unknown error',
    });
  } else {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_SNOOZE_RESULT_SUCCESS,
      filterUUID: response.data.uuid,
      key: pullRequestUUID,
    });
  }
}

export function* undoSnoozeStaticResult(action) {
  const { pullRequestUUID, filterUUID } = action;
  if (!pullRequestUUID || !filterUUID) {
    throw new Error(
      'Missing required parameters for re-enabling snoozed static result.'
    );
  }
  let url = `/pull-requests/${pullRequestUUID}/static-filters/${filterUUID}`;
  const response = yield call(Api.del, url);
  if (!response.success) {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_UNDO_SNOOZE_RESULT_FAILURE,
      error:
        response.data && response.data.error_message
          ? response.data.error_message
          : 'Unknown error',
    });
  } else {
    yield put({
      type: pullRequestsTypes.PULL_REQUEST_UNDO_SNOOZE_RESULT_SUCCESS,
    });
  }
}
